import { Plan } from "@/features/planner/api/planner";
import { useTranslation } from "react-i18next";

interface BudgetEstimateProps {
    plan: Plan;
}

export const BudgetEstimate = ({ plan }: BudgetEstimateProps) => {
    const { t } = useTranslation();
    if (!plan || !plan.itinerary || !plan.itinerary.days) return null;

    const { days } = plan.itinerary;
    const { budget_type, person_count, duration } = plan.preferences;
    const currency = plan.local_intelligence?.currency;
    const currencySymbol = currency?.symbol || "$";
    const currencyCode = currency?.code || "USD";

    // Helper to parse price string
    const parsePrice = (priceStr?: string): number => {
        if (!priceStr) return 0;
        if (priceStr.toLowerCase().includes("free")) return 0;
        const match = priceStr.match(/(\d+)/);
        return match ? parseInt(match[0], 10) : 0;
    };

    // Calculate Daily Activities Cost
    const dailyBreakdown = days.map(day => {
        let dayCost = 0;
        const slots = [day.morning, day.afternoon, day.evening];
        slots.forEach(slot => {
            if (!slot) return;
            slot.must_do?.forEach(act => {
                dayCost += parsePrice(act.price);
            });
            slot.side_activities?.forEach(act => {
                // Assuming side activities might be optional, but for a "high" estimate we can include them
                // or just keep it conservative. let's include for completeness of "Daily Breakdown"
                dayCost += parsePrice(act.price);
            });
        });
        return { day: day.day_count, cost: dayCost };
    });

    const totalActivitiesCost = dailyBreakdown.reduce((sum, item) => sum + item.cost, 0);
    const avgActivityCost = Math.round(totalActivitiesCost / duration);

    // Estimates per day based on budget type
    let foodPerDay = 40;
    let transportPerDay = 8;
    let accomPerNight = 120; // Default mid

    const budgetTypeLower = budget_type?.toLowerCase() || "standard";
    if (budgetTypeLower.includes("budget") || budgetTypeLower.includes("economy")) {
        foodPerDay = 25;
        transportPerDay = 5;
        accomPerNight = 60;
    } else if (budgetTypeLower.includes("luxury") || budgetTypeLower.includes("premium")) {
        foodPerDay = 100;
        transportPerDay = 25;
        accomPerNight = 250;
    }

    // Totals
    // Totals
    const totalFood = foodPerDay * duration;
    const totalTransport = transportPerDay * duration;

    const baseTotal = totalActivitiesCost + totalFood + totalTransport;

    // Accom cost calculation
    const accomCostPerPerson = (accomPerNight * duration) / (person_count > 1 ? 2 : 1);

    const minTotal = baseTotal + (accomCostPerPerson * 0.9);
    const maxTotal = baseTotal + (accomCostPerPerson * 1.1);

    const groupTotal = (maxTotal * person_count);

    return (
        <div className="mx-5 p-10 bg-white">
            <h2 className="text-center text-[28px] font-bold text-[#187A85] mb-6 font-serif">
                {t('planComponents.budgetEstimate.title')}
            </h2>

            {/* Total Estimated Cost Banner */}
            <div className="bg-[#187A85] text-white p-8 rounded-xl text-center mb-8 shadow-sm">
                <div className="text-[14px] font-bold uppercase tracking-widest opacity-90 mb-3">{t('planComponents.budgetEstimate.totalBudget')}</div>
                <div className="text-[48px] font-bold mb-2 leading-none">
                    {currencySymbol}{Math.round(minTotal).toLocaleString()} - {currencySymbol}{Math.round(maxTotal).toLocaleString()}
                </div>
                <div className="text-[16px] opacity-90 font-medium">
                    {t('planComponents.budgetEstimate.perPerson')}. {person_count > 1 ? `Group of ${person_count}: ${currencySymbol}${Math.round(groupTotal).toLocaleString()}` : ''}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {/* Accommodation */}
                <div className="bg-[#FFF9F5] rounded-xl p-6 h-full">
                    <h4 className="text-[18px] font-bold text-[#E76828] mb-4">{t('planComponents.budgetEstimate.accommodation')}</h4>
                    <div className="flex justify-between items-center text-[15px] border-b border-dashed border-[#EAEAEA] py-2">
                        <span className="text-[#6D6D6D] font-medium">Total</span>
                        <span className="font-bold text-[#222222]">{currencySymbol}{accomPerNight}/night ({budget_type})</span>
                    </div>
                </div>

                {/* Daily Breakdown */}
                <div className="bg-[#FFF9F5] rounded-xl p-6 h-full">
                    <h4 className="text-[18px] font-bold text-[#E76828] mb-4">Daily Breakdown</h4>
                    {dailyBreakdown.slice(0, 5).map((d, i) => (
                        <div key={i} className="flex justify-between items-center text-[15px] border-b border-dashed border-[#EAEAEA] py-2 last:border-0">
                            <span className="text-[#6D6D6D] font-medium">{t('planDetails.day')} {d.day}</span>
                            <span className="font-bold text-[#222222] text-right">{currencySymbol}{d.cost}</span>
                        </div>
                    ))}
                    {dailyBreakdown.length > 5 && (
                        <div className="text-[13px] text-center text-[#6D6D6D] mt-2 pt-2 border-t border-[#EAEAEA] border-dashed">
                            + {dailyBreakdown.length - 5} more days
                        </div>
                    )}
                </div>
            </div>

            {/* Money-Saving Tips */}
            <div className="bg-[#FFF9F5] rounded-xl p-6 mb-8">
                <h4 className="text-[18px] font-bold text-[#187A85] mb-4">Money-Saving Tips</h4>
                <div className="text-[15px] text-[#222222] flex flex-col gap-3">
                    <div className="flex items-start gap-2">
                        <span className="text-[#E76828] font-bold">→</span>
                        <span>Activities: <strong>{currencySymbol}{avgActivityCost}/day</strong> average based on your itinerary</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <span className="text-[#E76828] font-bold">→</span>
                        <span>Food: <strong>{currencySymbol}{foodPerDay}/day</strong> for {budget_type} dining in {plan.itinerary.destination}</span>
                    </div>
                    <div className="flex items-start gap-2">
                        <span className="text-[#E76828] font-bold">→</span>
                        <span>Transport: <strong>{currencySymbol}{transportPerDay}/day</strong> for public transit</span>
                    </div>
                </div>
            </div>

            <p className="text-[13px] text-[#6D6D6D] italic mt-4 opacity-80 text-center">
                {t('planComponents.budgetEstimate.note')}
            </p>
        </div>
    );
};
